import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../context/ThemeContext'; 
import Layout from '../../components/layout/Layout';
import ClientFeedbackModal from './ClientFeedbackModal'; 
import { API_BASE } from '../../api';

function ClientDashboard({ defaultTab = 'all' }) {
    const [tasks, setTasks] = useState([]);
    const [pendingTasks, setPendingTasks] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [refreshing, setRefreshing] = useState(false);
    const [exportingCSV, setExportingCSV] = useState(false);
    
    // State for the feedback modal
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);

    const navigate = useNavigate();
    const { currentTheme } = useContext(ThemeContext);

    const clientId = sessionStorage.getItem('userId');
    const clientName = sessionStorage.getItem('userName') || 'Client';

    // API base URL
    const API_BASE_URL = API_BASE;

    // 🎨 GLOBAL COLORS DEFINITION
    const colors = {
        backgroundDark: '#0f0c29', 
        backgroundMedium: '#302b63', 
        backgroundLight: '#24243e',
        textLight: 'rgba(255, 255, 255, 0.8)',
        borderDark: 'rgba(255, 255, 255, 0.2)',
        primary: currentTheme?.primary || '#6366F1',
        secondary: currentTheme?.secondary || '#8B5CF6',
        accent: currentTheme?.accent || '#06B6D4',
        success: '#10B981',
        warning: '#F59E0B',
        danger: '#EF4444',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
        gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
        gradient4: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' 
    };

    // CSV Export Function - Only exports visible columns
    const exportToCSV = () => {
        const tasksToExport = getTasksToDisplay();
        
        if (tasksToExport.length === 0) {
            setError('No data to export');
            return;
        }

        setExportingCSV(true);

        try {
            // Define CSV headers - Only the columns visible in the table
            const headers = [
                'Video Name',
                'Edited Link',
                'Thumbnail Link',
                'Status',
                'Shoot Date',
                'Action Status'
            ];
            
            // Prepare CSV data - Only the columns visible in the table
            const csvData = tasksToExport.map(task => {
                const editedLink = getEditedVideoLink(task);
                const hasValidEditedLink = isValidLink(editedLink);
                const shouldShowEdited = shouldShowEditedLink(task);
                const shouldShowThumb = shouldShowThumbnail(task);
                const isActionable = task.status === 'completed' || task.status === 'in_review';
                const isAlreadyProved = task.client_status === 'proved';
                
                // Get status text (matching the badge text)
                const statusConfig = {
                    unassigned: 'Unassigned',
                    assigned: 'Assigned',
                    in_progress: 'In Progress',
                    in_review: 'In Review',
                    needs_revision: 'Needs Revision',
                    completed: 'Completed',
                    approved: 'Approved',
                    cancelled: 'Cancelled',
                    pending: 'Pending'
                };
                
                const statusText = statusConfig[task.status] || 'Pending';
                
                // Determine action status
                let actionStatus = 'N/A';
                if (isActionable || isAlreadyProved) {
                    actionStatus = isAlreadyProved ? 'Approved/Proved' : 'Feedback Required';
                }
                
                return [
                    task.video_name || 'Untitled Project',
                    shouldShowEdited && hasValidEditedLink ? editedLink : 'Not Available',
                    shouldShowThumb && isValidLink(task.thumbnail_link) ? task.thumbnail_link : 'Not Available',
                    statusText,
                    formatDateForCSV(task.shoot_date),
                    actionStatus
                ];
            });

            // Create CSV content
            const csvContent = [
                headers.join(','),
                ...csvData.map(row => 
                    row.map(field => 
                        `"${String(field).replace(/"/g, '""')}"`
                    ).join(',')
                )
            ].join('\n');

            // Create blob and download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename
            const dateStr = new Date().toISOString().split('T')[0];
            const tabStr = activeTab === 'all' ? 'all' : activeTab;
            link.download = `client_projects_${clientName}_${tabStr}_${dateStr}.csv`;
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error('Error exporting CSV:', error);
            setError('Failed to export CSV: ' + error.message);
        } finally {
            setExportingCSV(false);
        }
    };

    // Helper function for CSV date formatting
    const formatDateForCSV = (dateString) => {
        if (!dateString || dateString === 'NULL' || dateString === '0000-00-00') return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'N/A';
        }
    };

    // Styles matching the consistent design
    const styles = {
        container: {
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
            position: 'relative',
            overflowX: 'hidden'
        },
        animatedBackground: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
        },
        floatingOrb: {
            position: 'absolute',
            borderRadius: '50%',
            filter: 'blur(40px)',
            opacity: 0.3,
            animation: 'float 8s ease-in-out infinite'
        },
        orb1: {
            width: '300px',
            height: '300px',
            background: colors.primary,
            top: '10%',
            left: '5%',
            animationDelay: '0s'
        },
        orb2: {
            width: '200px',
            height: '200px',
            background: colors.secondary,
            top: '60%',
            right: '10%',
            animationDelay: '2s'
        },
        orb3: {
            width: '250px',
            height: '250px',
            background: colors.accent,
            bottom: '20%',
            left: '15%',
            animationDelay: '4s'
        },
        orb4: {
            width: '180px',
            height: '180px',
            background: colors.success,
            top: '30%',
            right: '20%',
            animationDelay: '6s'
        },
        gridOverlay: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
        },
        header: {
            padding: '40px 40px 30px',
            position: 'relative',
            zIndex: 2
        },
        headerContent: {
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start'
        },
        greeting: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
            marginBottom: '15px'
        },
        pageTitle: {
            fontSize: '3rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0
        },
        welcomeBadge: {
            background: 'rgba(255, 255, 255, 0.1)',
            padding: '8px 16px',
            borderRadius: '20px',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        badgeText: {
            color: 'white',
            fontWeight: '600',
            fontSize: '0.9rem'
        },
        badgeRole: {
            background: colors.primary,
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.7rem',
            fontWeight: '600',
            textTransform: 'uppercase'
        },
        pageSubtitle: {
            color: colors.textLight,
            fontSize: '1.2rem',
            margin: 0,
            maxWidth: '600px'
        },
        headerActions: {
            display: 'flex',
            alignItems: 'center',
            gap: '20px'
        },
        refreshBtn: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            border: `1px solid ${colors.borderDark}`,
            padding: '12px 20px',
            borderRadius: '15px',
            cursor: 'pointer',
            fontWeight: '600',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: 'fit-content'
        },
        exportBtn: {
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '15px',
            cursor: 'pointer',
            fontWeight: '600',
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            height: 'fit-content'
        },
        refreshIcon: {
            fontSize: '1.1rem'
        },
        exportIcon: {
            fontSize: '1.1rem'
        },
        messageAlert: {
            color: 'white',
            padding: '20px',
            borderRadius: '15px',
            margin: '0 40px 30px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            maxWidth: '1200px',
            marginLeft: 'auto',
            marginRight: 'auto'
        },
        alertIcon: {
            fontSize: '1.5rem'
        },
        alertMessage: {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            color: 'white'
        },
        alertClose: {
            background: 'none',
            border: 'none',
            color: colors.danger,
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '8px',
            transition: 'background 0.3s ease'
        },
        content: {
            padding: '0 40px 40px',
            maxWidth: '1200px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 2
        },
        formCard: {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '25px',
            border: `1px solid ${colors.borderDark}`,
            backdropFilter: 'blur(10px)',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
        },
        cardHeader: {
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            padding: '25px 30px'
        },
        cardTitle: {
            fontSize: '1.8rem',
            fontWeight: '700',
            margin: 0
        },
        cardBody: {
            padding: '40px'
        },
        tabButton: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            border: `1px solid ${colors.borderDark}`,
            padding: '12px 24px',
            borderRadius: '12px',
            fontWeight: '600',
            fontSize: '1rem',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        activeTabButton: {
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: 'white',
            border: 'none'
        },
        loadingState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '100px 40px',
            color: 'white',
            textAlign: 'center'
        },
        loadingText: {
            fontSize: '2rem',
            margin: '0 0 20px 0',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
        },
        loadingSubtext: {
            color: colors.textLight,
            margin: 0,
            fontSize: '1.2rem'
        },
        spinner: {
            width: '20px',
            height: '20px',
            border: '2px solid transparent',
            borderTop: '2px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
        },
        tableHeader: {
            background: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            borderBottom: `2px solid ${colors.borderDark}`,
            padding: '15px 12px',
            fontWeight: '600'
        },
        tableRow: {
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            color: 'white'
        },
        tableCell: {
            padding: '12px',
            verticalAlign: 'middle'
        },
        statusBadge: {
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px'
        }
    };

    useEffect(() => {
        setActiveTab(defaultTab);
    }, [defaultTab]);

    useEffect(() => {
        fetchClientTasks();
    }, []);

    const fetchClientTasks = async () => {
        try {
            setLoading(true);
            setError('');
            
            console.log('🔄 Fetching client tasks for client ID:', clientId);
            
            const response = await fetch(`${API_BASE_URL}/get_client_editor_tasks.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: parseInt(clientId)
                })
            });

            if (!response.ok) { 
                throw new Error(`HTTP error! status: ${response.status}`); 
            }
            
            const data = await response.json();
            
            if (!data.success) { 
                throw new Error(data.error || 'Failed to fetch tasks'); 
            }

            const allTasks = Array.isArray(data.tasks) ? data.tasks : [];
            
            // DEBUG: Check task data
            console.log('📊 All tasks received:', allTasks.length);
            if (allTasks.length > 0) {
                console.log('🔍 Sample task data:', allTasks[0]);
                console.log('🔍 All fields in first task:', Object.keys(allTasks[0]));
                console.log('🔍 Edited link field values:', {
                    edited_video_link: allTasks[0].edited_video_link,
                    edited_link: allTasks[0].edited_link,
                    raw_link: allTasks[0].raw_link,
                    hasEditedVideoLink: !!allTasks[0].edited_video_link,
                    hasEditedLink: !!allTasks[0].edited_link,
                    hasRawLink: !!allTasks[0].raw_link
                });
            }

            const pending = allTasks.filter(task => 
                task.status !== 'completed' && 
                task.status !== 'approved' && 
                task.status !== 'cancelled'
            );
            const completed = allTasks.filter(task => 
                task.status === 'completed' || 
                task.status === 'approved'
            );

            setTasks(allTasks);
            setPendingTasks(pending);
            setCompletedTasks(completed);

        } catch (error) {
            console.error('❌ Error fetching client tasks:', error);
            setError('Error loading tasks: ' + error.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // NEW: Handle refresh button click
    const handleRefresh = () => {
        setRefreshing(true);
        fetchClientTasks();
    };

    // --- Action Handlers ---

    const handleFeedbackClick = (task) => {
        console.log('Opening feedback modal for task:', task.id);
        setSelectedTask(task);
        setShowFeedbackModal(true);
    };

    const handleFeedbackSubmit = async (taskId, provedStatus, description, result) => {
        console.log('Feedback submitted successfully:', { taskId, provedStatus, description, result });
        // Refresh tasks to show updated status
        await fetchClientTasks();
        setShowFeedbackModal(false);
        setSelectedTask(null);
    };

    const handleFeedbackClose = () => {
        console.log('Closing feedback modal');
        setShowFeedbackModal(false);
        setSelectedTask(null);
    };

    const handleLinkClick = (link) => {
        if (isValidLink(link)) {
            console.log('Opening link:', link);
            let formattedLink = link;
            if (!formattedLink.startsWith('http://') && !formattedLink.startsWith('https://')) {
                formattedLink = 'https://' + formattedLink;
            }
            window.open(formattedLink, '_blank', 'noopener,noreferrer');
        } else {
            console.log('Invalid link:', link);
        }
    };

    // --- Helper Functions ---
    const isValidLink = (link) => {
        if (!link || typeof link !== 'string') {
            return false;
        }
        
        const trimmedLink = link.trim();
        const invalidValues = ['-', 'Open', 'NULL', 'null', '', 'N/A', 'undefined', 'None'];
        
        if (invalidValues.includes(trimmedLink)) {
            return false;
        }
        
        if (trimmedLink.length < 5) { // Minimum reasonable URL length
            return false;
        }
        
        return true;
    };

    // ✅ FIXED: Function to get edited video link from any available field
    const getEditedVideoLink = (task) => {
        // Check multiple possible field names for edited video link
        const editedLink = 
            task.edited_video_link ||  // From tasks table
            task.edited_link ||        // From editor_tasks table (aliased in API)
            task.raw_link;             // From editor_tasks table (might be misnamed)
        
        console.log(`🔍 Getting edited link for task ${task.id}:`, {
            edited_video_link: task.edited_video_link,
            edited_link: task.edited_link,
            raw_link: task.raw_link,
            finalLink: editedLink,
            isValid: isValidLink(editedLink)
        });
        
        return editedLink;
    };

    // ✅ FIXED: Function to check if edited link should be shown
    const shouldShowEditedLink = (task) => {
        const editedLink = getEditedVideoLink(task);
        const hasValidLink = isValidLink(editedLink);
        
        // Show edited link if:
        // 1. We have a valid link AND
        // 2. Task is in a state where client can view it
        const isViewableState = 
            task.status === 'completed' || 
            task.status === 'approved' || 
            task.status === 'in_review' ||
            task.client_status === 'proved' ||
            (task.toggle_status && task.toggle_status === 'proved');
        
        const shouldShow = hasValidLink && isViewableState;
        
        console.log(`📹 Edited link display for task ${task.id}:`, { 
            taskId: task.id,
            video_name: task.video_name,
            status: task.status,
            client_status: task.client_status,
            toggle_status: task.toggle_status,
            hasValidLink: hasValidLink,
            isViewableState: isViewableState,
            shouldShow: shouldShow
        });
        
        return shouldShow;
    };

    // Function to check if thumbnail should be shown
    const shouldShowThumbnail = (task) => {
        // Show thumbnail for completed/approved tasks OR if thumbnail exists
        const shouldShow = (task.status === 'completed' || task.status === 'approved' || isValidLink(task.thumbnail_link));
        const hasValidThumbnail = isValidLink(task.thumbnail_link);
        
        return shouldShow && hasValidThumbnail;
    };

    // ✅ UPDATED: Custom status badge function with better styling
    const getStatusBadge = (status) => {
        const statusConfig = {
            unassigned: { class: 'secondary', text: 'Unassigned', icon: 'fa-user-times' },
            assigned: { class: 'primary', text: 'Assigned', icon: 'fa-user-check' },
            in_progress: { class: 'info', text: 'In Progress', icon: 'fa-spinner' },
            in_review: { class: 'warning', text: 'In Review', icon: 'fa-eye' },
            needs_revision: { class: 'warning', text: 'Needs Revision', icon: 'fa-sync' },
            completed: { class: 'success', text: 'Completed', icon: 'fa-check' }, 
            approved: { class: 'success', text: 'Approved', icon: 'fa-check-circle' }, 
            cancelled: { class: 'danger', text: 'Cancelled', icon: 'fa-times' },
            pending: { class: 'warning', text: 'Pending', icon: 'fa-clock' }
        };
        
        const config = statusConfig[status] || statusConfig.pending;
        const colorMap = {
            primary: colors.primary,
            secondary: colors.secondary,
            success: colors.success,
            danger: colors.danger,
            warning: colors.warning,
            info: colors.accent
        };
        
        return (
            <span style={{
                ...styles.statusBadge,
                background: colorMap[config.class],
                color: config.class === 'warning' ? '#000' : '#fff'
            }}>
                <i className={`fas ${config.icon}`}></i>
                {config.text}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === 'NULL') return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getTasksToDisplay = () => {
        switch(activeTab) {
            case 'pending': return pendingTasks;
            case 'completed': return completedTasks;
            default: return tasks;
        }
    };

    const getPageTitle = () => {
        switch(activeTab) {
            case 'pending': return 'Pending Projects';
            case 'completed': return 'Completed Projects';
            default: return 'All Projects';
        }
    };

    if (loading && !refreshing) {
        return (
            <Layout>
                <div style={styles.container}>
                    <div className="animated-background" style={styles.animatedBackground}>
                        <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                        <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                        <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                        <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                        <div className="grid-overlay" style={styles.gridOverlay}></div>
                    </div>
                    <div style={styles.loadingState}>
                        <div className="loading-spinner" style={styles.spinner}></div>
                        <h3 style={styles.loadingText}>Loading Your Projects</h3>
                        <p style={styles.loadingSubtext}>Please wait while we fetch your project information</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={styles.container}>
                {/* Animated Background */}
                <div className="animated-background" style={styles.animatedBackground}>
                    <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
                    <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
                    <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
                    <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
                    <div className="grid-overlay" style={styles.gridOverlay}></div>
                </div>

                {/* Header Section */}
                <div className="client-dashboard-header" style={styles.header}>
                    <div className="header-content" style={styles.headerContent}>
                        <div className="welcome-section">
                            <div className="greeting" style={styles.greeting}>
                                <h1 className="page-title" style={styles.pageTitle}>
                                    <i className="fas fa-user-circle me-2"></i>
                                    Client Dashboard
                                </h1>
                                <div className="welcome-badge" style={styles.welcomeBadge}>
                                    <span className="badge-text" style={styles.badgeText}>Project Management</span>
                                    <span className="badge-role" style={styles.badgeRole}>Client</span>
                                </div>
                            </div>
                            <p className="page-subtitle" style={styles.pageSubtitle}>
                                Welcome back, {clientName}! Manage and review your video projects
                                {tasks.length > 0 && (
                                    <span className="ms-2 badge" style={{background: colors.primary, color: 'white'}}>
                                        {tasks.length} total projects
                                    </span>
                                )}
                            </p>
                        </div>
                        
                        {/* Header Actions with Export CSV Button */}
                        <div className="header-actions" style={styles.headerActions}>
                            <button 
                                className="export-btn"
                                onClick={exportToCSV}
                                disabled={exportingCSV || getTasksToDisplay().length === 0}
                                style={{
                                    ...styles.exportBtn,
                                    opacity: exportingCSV || getTasksToDisplay().length === 0 ? 0.6 : 1,
                                    cursor: exportingCSV || getTasksToDisplay().length === 0 ? 'not-allowed' : 'pointer'
                                }}
                                title={getTasksToDisplay().length === 0 ? "No data to export" : "Export to CSV"}
                            >
                                <div className="export-icon" style={styles.exportIcon}>
                                    <i className={`fas fa-file-export ${exportingCSV ? 'spinning' : ''}`}></i>
                                </div>
                                <span>{exportingCSV ? 'Exporting...' : 'Export CSV'}</span>
                            </button>
                            
                            <button 
                                className="refresh-btn"
                                onClick={handleRefresh}
                                disabled={refreshing}
                                style={{
                                    ...styles.refreshBtn,
                                    opacity: refreshing ? 0.7 : 1,
                                    cursor: refreshing ? 'not-allowed' : 'pointer'
                                }}
                                title="Refresh project data"
                            >
                                <div className="refresh-icon" style={styles.refreshIcon}>
                                    <i className={`fas fa-sync ${refreshing ? 'spinning' : ''}`}></i>
                                </div>
                                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Message Alert */}
                {error && (
                    <div className="message-alert" style={{
                        ...styles.messageAlert,
                        background: 'rgba(220, 53, 69, 0.1)',
                        border: `1px solid ${colors.danger}`,
                        color: colors.danger
                    }}>
                        <div className="alert-icon" style={{...styles.alertIcon, color: colors.danger}}>
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>Error</strong>
                            <span style={{color: 'white'}}>{error}</span>
                        </div>
                        <button className="alert-close" onClick={() => setError('')} style={styles.alertClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                )}

                {/* Success Message when refreshed */}
                {refreshing && !error && (
                    <div className="message-alert" style={{
                        ...styles.messageAlert,
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: `1px solid ${colors.success}`,
                        color: colors.success
                    }}>
                        <div className="alert-icon" style={{...styles.alertIcon, color: colors.success}}>
                            <i className="fas fa-sync"></i>
                        </div>
                        <div className="alert-message" style={styles.alertMessage}>
                            <strong>Refreshing</strong>
                            <span style={{color: 'white'}}>Updating your project data...</span>
                        </div>
                    </div>
                )}

              
                {/* Main Content */}
                <div className="client-dashboard-content" style={styles.content}>
                    {/* Tab Navigation */}
                    <div style={{display: 'flex', gap: '15px', marginBottom: '30px', flexWrap: 'wrap'}}>
                        <button
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === 'all' ? styles.activeTabButton : {})
                            }}
                            onClick={() => setActiveTab('all')}
                        >
                            <i className="fas fa-list me-2"></i>
                            All Projects
                            <span className="badge" style={{
                                background: activeTab === 'all' ? 'rgba(255,255,255,0.2)' : colors.primary,
                                color: 'white',
                                marginLeft: '8px'
                            }}>
                                {tasks.length}
                            </span>
                        </button>
                        <button
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === 'pending' ? {
                                    background: `linear-gradient(135deg, ${colors.warning}, #eab308)`,
                                    color: 'white',
                                    border: 'none'
                                } : {})
                            }}
                            onClick={() => setActiveTab('pending')}
                        >
                            <i className="fas fa-clock me-2"></i>
                            Pending
                            <span className="badge" style={{
                                background: activeTab === 'pending' ? 'rgba(255,255,255,0.2)' : colors.warning,
                                color: 'white',
                                marginLeft: '8px'
                            }}>
                                {pendingTasks.length}
                            </span>
                        </button>
                        <button
                            style={{
                                ...styles.tabButton,
                                ...(activeTab === 'completed' ? {
                                    background: `linear-gradient(135deg, ${colors.success}, ${colors.accent})`,
                                    color: 'white',
                                    border: 'none'
                                } : {})
                            }}
                            onClick={() => setActiveTab('completed')}
                        >
                            <i className="fas fa-check-circle me-2"></i>
                            Completed
                            <span className="badge" style={{
                                background: activeTab === 'completed' ? 'rgba(255,255,255,0.2)' : colors.success,
                                color: 'white',
                                marginLeft: '8px'
                            }}>
                                {completedTasks.length}
                            </span>
                        </button>
                    </div>

                    {/* Projects Card */}
                    <div className="projects-card" style={styles.formCard}>
                        <div className="card-header" style={styles.cardHeader}>
                            <h3 style={styles.cardTitle}>
                                <i className="fas fa-list me-2"></i>
                                {getPageTitle()}
                                {getTasksToDisplay().length > 0 && (
                                    <span style={{
                                        fontSize: '0.9rem',
                                        marginLeft: '10px',
                                        fontWeight: '400',
                                        opacity: 0.9
                                    }}>
                                        ({getTasksToDisplay().length} projects - Ready for CSV export)
                                    </span>
                                )}
                            </h3>
                        </div>
                        <div style={{padding: '0'}}>
                            {getTasksToDisplay().length === 0 ? (
                                <div style={{padding: '60px 40px', textAlign: 'center'}}>
                                    <i className="fas fa-inbox fa-4x mb-3" style={{color: `${colors.textLight}20`}}></i>
                                    <h4 style={{color: colors.textLight, marginBottom: '15px'}}>No projects found</h4>
                                    <p style={{color: colors.textLight, marginBottom: '25px'}}>
                                        No projects found for this status.
                                    </p>
                                    <button 
                                        onClick={handleRefresh}
                                        style={{
                                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <i className="fas fa-sync"></i>
                                        Refresh
                                    </button>
                                </div>
                            ) : (
                                <div style={{overflowX: 'auto'}}>
                                    <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse'}}>
                                        <thead>
                                            <tr>
                                                <th style={styles.tableHeader}>
                                                    <i className="fas fa-film me-2"></i>
                                                    Video Name
                                                </th>
                                                <th style={styles.tableHeader}>
                                                    <i className="fas fa-link me-2"></i>
                                                    Edited Link
                                                </th>
                                                <th style={styles.tableHeader}>
                                                    <i className="fas fa-image me-2"></i>
                                                    Thumbnail Link
                                                </th>
                                                <th style={styles.tableHeader}>
                                                    <i className="fas fa-flag me-2"></i>
                                                    Status
                                                </th>
                                                <th style={styles.tableHeader}>
                                                    <i className="fas fa-calendar me-2"></i>
                                                    Shoot Date
                                                </th>
                                                <th style={styles.tableHeader}>
                                                    <i className="fas fa-cogs me-2"></i>
                                                    Action
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {getTasksToDisplay().map((task) => {
                                                // Action is required if the video is ready for client review/approval
                                                const isActionable = task.status === 'completed' || task.status === 'in_review';
                                                
                                                // Check if client_status is 'proved'
                                                const isAlreadyProved = task.client_status === 'proved';

                                                // ✅ FIXED: Get the edited video link
                                                const editedVideoLink = getEditedVideoLink(task);
                                                const shouldShowEdited = shouldShowEditedLink(task);
                                                const hasValidEditedLink = isValidLink(editedVideoLink);

                                                // Check if thumbnail should be shown
                                                const shouldShowThumb = shouldShowThumbnail(task);
                                                const hasValidThumbnail = shouldShowThumb && isValidLink(task.thumbnail_link);

                                                // Determine button content
                                                const buttonContent = isAlreadyProved 
                                                    ? <>✅ Proved</>
                                                    : <> <i className="fas fa-plus me-1"></i> Add Feedback</>;
                                                
                                                // Determine button style
                                                const buttonStyle = isAlreadyProved 
                                                    ? {
                                                        background: colors.success,
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '600',
                                                        cursor: 'default',
                                                        opacity: 0.7
                                                    }
                                                    : {
                                                        background: `linear-gradient(135deg, ${colors.success}, ${colors.accent})`,
                                                        color: 'white',
                                                        border: 'none',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.3s ease'
                                                    };

                                                return (
                                                    <tr key={task.id} style={styles.tableRow}>
                                                        {/* Video Name */}
                                                        <td style={styles.tableCell}>
                                                            <span style={{ fontWeight: '600', color: 'white' }}>
                                                                <i className="fas fa-video me-2" style={{color: colors.textLight}}></i>
                                                                {task.video_name || 'Untitled Project'}
                                                            </span>
                                                        </td>
                                                        {/* ✅ FIXED: Edited Link Column */}
                                                        <td style={styles.tableCell}>
                                                            {shouldShowEdited ? (
                                                                <button
                                                                    style={{
                                                                        background: hasValidEditedLink 
                                                                            ? `linear-gradient(135deg, ${colors.success}, ${colors.accent})`
                                                                            : 'rgba(255, 255, 255, 0.1)',
                                                                        color: 'white',
                                                                        border: `1px solid ${colors.borderDark}`,
                                                                        padding: '6px 12px',
                                                                        borderRadius: '8px',
                                                                        cursor: hasValidEditedLink ? 'pointer' : 'not-allowed',
                                                                        opacity: hasValidEditedLink ? 1 : 0.6,
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: '600',
                                                                        transition: 'all 0.3s ease'
                                                                    }}
                                                                    onClick={() => handleLinkClick(editedVideoLink)}
                                                                    disabled={!hasValidEditedLink}
                                                                    title={hasValidEditedLink ? `View edited video: ${editedVideoLink}` : 'No edited link available'}
                                                                    onMouseEnter={(e) => {
                                                                        if (hasValidEditedLink) {
                                                                            e.target.style.transform = 'translateY(-2px)';
                                                                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        if (hasValidEditedLink) {
                                                                            e.target.style.transform = 'translateY(0)';
                                                                            e.target.style.boxShadow = 'none';
                                                                        }
                                                                    }}
                                                                >
                                                                    <i className="fas fa-video me-1"></i>
                                                                    {hasValidEditedLink ? 'View' : 'No Link'}
                                                                </button>
                                                            ) : (
                                                                <span style={{ 
                                                                    color: colors.textLight, 
                                                                    fontSize: '0.8rem',
                                                                    fontStyle: 'italic'
                                                                }}>
                                                                    <i className="fas fa-clock me-1"></i>
                                                                    Not Ready
                                                                </span>
                                                            )}
                                                        </td>
                                                        {/* Thumbnail URL Column */}
                                                        <td style={styles.tableCell}>
                                                            {shouldShowThumb ? (
                                                                <button
                                                                    style={{
                                                                        background: hasValidThumbnail 
                                                                            ? `linear-gradient(135deg, ${colors.warning}, #eab308)`
                                                                            : 'rgba(255, 255, 255, 0.1)',
                                                                        color: 'white',
                                                                        border: `1px solid ${colors.borderDark}`,
                                                                        padding: '6px 12px',
                                                                        borderRadius: '8px',
                                                                        cursor: hasValidThumbnail ? 'pointer' : 'not-allowed',
                                                                        opacity: hasValidThumbnail ? 1 : 0.6,
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: '600'
                                                                    }}
                                                                    onClick={() => handleLinkClick(task.thumbnail_link)}
                                                                    disabled={!hasValidThumbnail}
                                                                    title={hasValidThumbnail ? task.thumbnail_link : 'No thumbnail available'}
                                                                >
                                                                    <i className="fas fa-image me-1"></i>
                                                                    {hasValidThumbnail ? 'View' : 'No Thumbnail'}
                                                                </button>
                                                            ) : (
                                                                <span style={{ 
                                                                    color: colors.textLight, 
                                                                    fontSize: '0.8rem',
                                                                    fontStyle: 'italic'
                                                                }}>
                                                                    <i className="fas fa-clock me-1"></i>
                                                                    Not Ready
                                                                </span>
                                                            )}
                                                        </td>
                                                        {/* Status Column */}
                                                        <td style={styles.tableCell}>
                                                            {getStatusBadge(task.status)}
                                                        </td>
                                                        {/* Shoot Date */}
                                                        <td style={styles.tableCell}>
                                                            <small style={{ color: colors.textLight }}>
                                                                <i className="fas fa-calendar me-1"></i>
                                                                {formatDate(task.shoot_date)}
                                                            </small>
                                                        </td>
                                                        {/* Action Column */}
                                                        <td style={styles.tableCell}>
                                                            {isActionable || isAlreadyProved ? (
                                                                <button
                                                                    style={buttonStyle}
                                                                    onClick={() => {
                                                                        if (!isAlreadyProved) {
                                                                            handleFeedbackClick(task);
                                                                        }
                                                                    }}
                                                                    disabled={isAlreadyProved}
                                                                    title={isAlreadyProved ? "Already approved" : "Submit feedback"}
                                                                    onMouseEnter={(e) => {
                                                                        if (!isAlreadyProved) {
                                                                            e.target.style.transform = 'translateY(-2px)';
                                                                            e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                                                                        }
                                                                    }}
                                                                    onMouseLeave={(e) => {
                                                                        if (!isAlreadyProved) {
                                                                            e.target.style.transform = 'translateY(0)';
                                                                            e.target.style.boxShadow = 'none';
                                                                        }
                                                                    }}
                                                                >
                                                                    {buttonContent}
                                                                </button>
                                                            ) : (
                                                                <span style={{ color: colors.textLight, fontSize: '0.9rem' }}>
                                                                    <i className="fas fa-minus me-1"></i>
                                                                    N/A
                                                                </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Feedback Modal */}
            <ClientFeedbackModal
                show={showFeedbackModal}
                onClose={handleFeedbackClose}
                onSubmit={handleFeedbackSubmit}
                task={selectedTask}
                primaryColor={colors.primary}
                currentClientId={parseInt(clientId)}
            />

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes float {
                    0%, 100% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                }

                .spinning {
                    animation: spin 1s linear infinite;
                }

                .table {
                    --bs-table-bg: transparent;
                    --bs-table-color: white;
                }

                .table-hover tbody tr:hover {
                    background: rgba(255, 255, 255, 0.05) !important;
                }

                .refresh-btn:hover {
                    background: rgba(255, 255, 255, 0.15) !important;
                    transform: translateY(-2px);
                    boxShadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                }

                .export-btn:hover:not(:disabled) {
                    background: linear-gradient(135deg, ${colors.secondary}, ${colors.primary}) !important;
                    transform: translateY(-2px);
                    boxShadow: 0 5px 15px rgba(0, 0, 0, 0.2);
                }
            `}</style>
        </Layout>
    );
}

export default ClientDashboard;